import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Loading from "../../Common/Loading";
import CustomAlert from "../../Common/CustomAlert";

import "./Style/StillReport.css";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK 

interface BrickData {
  Year: number;
  DisplayYear: number;
  ScoreAchieved: number;
  YearLongForm: string;
  YearShortForm: string;
  IsStudyYear: boolean;
  IsFutureYear: boolean;
  Improvement: string;
  ImprovementColor: string;
}

interface GapData {
  DisplayYear: number;
  Gaps: string;
}

interface ReportData {
  student_info: { FName: string; LName: string };
  report_meta: {
    Country: string;
    IsUSOrCanada: boolean;
    IsNZ: boolean;
    StudyYear: number;
    DisplayStudyYear: number;
    TestCompletionDate: string;
    GradeAchieved: number;
    GradeLabel: string;
  };
  brickwall: BrickData[];
  gaps: GapData[];
  summary_text: string;
  recommendation_text: string;
}

const EStillReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [loading, setLoading] = useState(true);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: "" });
  const [data, setData] = useState<ReportData | null>(null);

  const schoolCode = searchParams.get("SchoolCode") || "";
  const familyCode = searchParams.get("FamilyCode") || "";
  const loginId = searchParams.get("Login") || "";
  const reportNo = searchParams.get("ReportNo") || "";
    useEffect(()=>{
    document.body.style.backgroundColor="none"
  })
 

  useEffect(() => {
     document.body.style.backgroundColor="none"
    const fetchReport = async () => {
      if (!schoolCode || !familyCode || !loginId || !reportNo) {
        setAlertInfo({
          show: true,
          message:
            "Unable to show Report due to unavailability of correct data.",
        });
        setLoading(false);
        return;
      }
      try {
        const query = new URLSearchParams({
          SchoolCode: schoolCode,
          FamilyCode: familyCode,
          Login: loginId,
          ReportNo: reportNo,
        });
        const res = await fetch(`${API_BASE_URL}api/get_still_report_EC?${query}`);
        const result = await res.json();

        if (result.status === "success") {
          setData(result.data);
        } else {
          setAlertInfo({
            show: true,
            message:
              result.message ||
              "Failed to load report. Please contact customer support.",
          });
        }
      } catch (err) {
        setAlertInfo({
          show: true,
          message: "Network error while fetching report.",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [schoolCode, familyCode, loginId, reportNo]);

  useEffect(() => {
    if (data) {
      const isUS = data.report_meta.IsUSOrCanada;
      document.body.className = `ctry ${isUS ? "us" : "au"} still`;
    }
    return () => {
      document.body.className = "";
    };
  }, [data]);

  useEffect(() => {
    if (!data || !canvasRef.current) return;

    const bricks = data.brickwall;

    const CanvasWidth = 800;
    const BrickHeight = 41.81;
    const BrickWidth = CanvasWidth / 8;
    const CanvasHeight = BrickHeight * bricks.length;

    const canvas = canvasRef.current;
    canvas.width = CanvasWidth;
    canvas.height = CanvasHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CanvasWidth, CanvasHeight);

    bricks.forEach((brick, i) => {
      const yPos = i * BrickHeight;
      const percentageRight = (100 - brick.ScoreAchieved) / 2 / 100;
      const percentageLeft = (100 - brick.ScoreAchieved) / 2 / 100;

      ctx.fillStyle = brick.IsFutureYear ? "#fbc776" : "#F9A31C";
      ctx.fillRect(0, yPos, CanvasWidth, BrickHeight);

      ctx.fillStyle = brick.IsFutureYear ? "#e76666" : "#d70000";
      const center = CanvasWidth / 2;
      ctx.fillRect(center, yPos, percentageRight * CanvasWidth, BrickHeight);
      ctx.fillRect(
        center - percentageLeft * CanvasWidth,
        yPos,
        percentageLeft * CanvasWidth,
        BrickHeight,
      );

      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.moveTo(0, yPos);
      ctx.lineTo(CanvasWidth, yPos);
      ctx.stroke();

      const rowFromBottom = bricks.length - i;
      let x = rowFromBottom % 2 !== 0 ? 0 : -(BrickWidth / 2);
      ctx.beginPath();
      while (x < CanvasWidth) {
        x += BrickWidth;
        ctx.moveTo(x, yPos);
        ctx.lineTo(x, yPos + BrickHeight);
        ctx.stroke();
      }
    });
  }, [data]);

  const handlePrint = () => {
    navigate(
      `/MathsDoctor/Reports/PDFReport?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${loginId}&ReportNo=${reportNo}`
    );
  };

  if (loading) return <Loading />;
  if (!data)
    return (
      <>
        {alertInfo.show && (
          <CustomAlert
            message={alertInfo.message}
            onClose={() => setAlertInfo({ ...alertInfo, show: false })}
          />
        )}
      </>
    );

  const {
    student_info,
    report_meta,
    brickwall,
    gaps,
    summary_text,
    recommendation_text,
  } = data;
  const showFutureNote = brickwall.some((b) => b.IsFutureYear);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css?family=Quicksand"
        rel="stylesheet"
        type="text/css"
      />
      <link
        href="https://fonts.googleapis.com/css?family=Open+Sans"
        rel="stylesheet"
        type="text/css"
      />

      <div id="page_wrap">
        <div id="header_bar" style={{position:'relative'}}>
          <div id="top_logo_box_left"></div>
          <div id="top_logo_box"></div>
          <div id="top_text_div" style={{width:"50%"}}>
            <div id="top_name">
              <p>
                <span className="firstname" id="spanFName">
                  {student_info.FName}
                </span>
                &nbsp;
                <span className="lastname" id="spanLName">
                  {student_info.LName}
                </span>
              </p>
            </div>
            <div id="top_title">
              <p style={{ fontWeight: "normal" }}>Knowledge of Curriculum Report</p>
            </div>
            <div className="top_details">
              <p style={{ fontWeight: "normal" }}>
                School Level:{" "}
                <span id="spanGradeText" className="ctry us">
                  Grade
                </span>
                <span id="spanGradeText_au" className="ctry au">
                  Year
                </span>{" "}
                <span id="spanGrade">{report_meta.DisplayStudyYear}</span> /
                Assessment Completed:{" "}
                <span id="spanCompletionDate">
                  {report_meta.TestCompletionDate}
                </span>
              </p>
            </div>
            <div className="top_details2">
              <p>
                School Level:{" "}
                <span id="spanGradeText1" className="ctry us">
                  Grade
                </span>
                <span id="spanGradeText1_au" className="ctry au">
                  Year
                </span>{" "}
                <span id="spanGrade1">{report_meta.DisplayStudyYear}</span>
              </p>
            </div>
            <div className="top_details2">
              <p>
                Assessment Completed:{" "}
                <span id="spanCompletionDate1">
                  {report_meta.TestCompletionDate}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div id="report_container">
          <hr id="hr_div" />
          <div id="wall_container">
            <div className="summary" style={{marginTop:"0"}}>
              <p style={{marginTop:"20px", marginLeft:"-15%", marginBottom:"10px"}}>Summary</p>
              <div>
                <span
                  id="spanSummaryText"
                  dangerouslySetInnerHTML={{ __html: summary_text }}
                ></span>
              </div>
              <div>
                <span className="recommendation-title" style={{fontSize:"14px"}}>
                  Recommendation: &nbsp;
                </span>
                <span
                  id="spanRecommendationText"
                  dangerouslySetInnerHTML={{ __html: recommendation_text }}
                ></span>
              </div>
            </div>

            <div className="wall-heading" style={{marginBottom:"10px", marginLeft:"25%"}}>Result</div>

            <div className="brickwall-visual">
              <div className="grades" id='grades' >
                {brickwall.map((b) => (
                  <div
                    className="graph_row"
                    id={`divRow${b.Year}`}
                    style={{ display: "block" }}
                    key={`L${b.Year}`}
                  >
                    <div
                      id={`whitetext${b.Year}`}
                      className="whiteout_row"
                      style={{ opacity: b.IsFutureYear ? 0.5 : 0, height:"0", width:"0", position:"unset" }}
                    ></div>
                    <div className="grade_box">
                      <p
                        id={`pCurrentGradeRow${b.Year}`}
                        className={b.IsStudyYear ? "grade_current_text" : ""}
                        style={{
                          color: b.IsStudyYear ? "#2579D3" : "black",
                          backgroundColor: b.IsStudyYear ? "#DCEAF8" : "transparent"
                        }}
                      >
                        {b.YearLongForm}
                      </p>
                    </div>
                    <div className="grade_box2">
                      <p
                        id={`pCurrentGradeRow${b.Year}1`}
                        className={b.IsStudyYear ? "grade_current_text" : ""}
                        style={{
                          color: b.IsStudyYear ? "#2579D3" : "black",
                          backgroundColor: b.IsStudyYear ? "#DCEAF8" : "transparent"
                        }}
                      >
                        {b.YearShortForm}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div id="graph">
                <canvas id="canvasBrickWall" ref={canvasRef}></canvas>
              </div>
            </div>

            <div className="extras" style={{marginLeft:"-5%"}}>
              <div className="legend-container" style={{marginLeft:"25%"}}>
                <div className="legend" >
                  <div className="legend-heading">Brick wall legend:</div>
                  <div className="legend-div">
                    <div className="yellow-icon"></div>
                    <div className="legend-text">Solid understanding</div>
                  </div>
                  <div className="legend-div">
                    <div className="red-icon"></div>
                    <div className="legend-text">Knowledge gaps</div>
                  </div>
                  <div
                    className="brick-note"
                    style={{ display: showFutureNote ? "block" : "none" }}
                  >
                    &#42; Note that where the bricks are faded, this represents{" "}
                    <strong>
                      future <span className="ctry au">years</span>
                      <span className="ctry us">grades</span>
                    </strong>
                    .
                  </div>
                </div>
              </div>

              <div className="achieve-container" style={{marginLeft:"-10%"}}>
                <div className="grade-achieved" style={{marginLeft:"10px"}}>
                  <span className="ctry au">Year</span>
                  <span className="ctry us">Grade</span> Levels Achieved
                </div>

                <div
                  className="bar-container"
                  id="LevelAchieved"
                  style={{ display: report_meta.IsNZ ? "none" : "block" }}
                >
                  <div className="progressbar">
                    <div
                      className="grey-block"
                      id="greymask"
                      style={{
                        width: `${100 - report_meta.GradeAchieved * 10}%`,
                      }}
                    ></div>
                    <div className="green-block box_wrapper_front" id="block1">
                      <div className="top_bar_num_first">0</div>
                      <div className="top_bar_num">1</div>
                    </div>
                    {[2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <div className="green-block" id={`block${num}`} key={num}>
                        <div className="top_bar_num">{num}</div>
                      </div>
                    ))}
                    <div className="green-block box_wrapper_end" id="block10">
                      <div className="top_bar_num">10</div>
                    </div>
                  </div>
                </div>

                <div
                  className="bar-container_NZ"
                  id="LevelAchieved_NZ"
                  style={{ display: report_meta.IsNZ ? "block" : "none" }}
                >
                  <div className="progressbar">
                    <div
                      className="grey-block"
                      id="greymask_NZ"
                      style={{
                        width: `${100 - report_meta.GradeAchieved * 10}%`,
                      }}
                    ></div>
                    <div
                      className="green-block box_wrapper_front"
                      id="block1_NZ"
                    >
                      <div className="top_bar_num_first">1</div>
                      <div className="top_bar_num">2</div>
                    </div>
                    {[3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <div
                        className="green-block"
                        id={`block${num - 1}_NZ`}
                        key={num}
                      >
                        <div className="top_bar_num">{num}</div>
                      </div>
                    ))}
                    <div
                      className="green-block box_wrapper_end"
                      id="block10_NZ"
                    >
                      <div className="top_bar_num">11</div>
                    </div>
                  </div>
                </div>
              </div>

              <div id="get_started_div">
                <div id="get_started_btn" onClick={() => alert("get started")}>
                  <div id="plus"></div>
                  <div id="plus_text">
                    <p>GET STARTED</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="gaps_container">
          <div
            id="print_btn"
            style={{ display: "block", marginBottom: "20px" }}
            onClick={handlePrint}
          >
            <p>PRINT REPORT</p>
          </div>
          <p id="gaps_title">
            <span className="ctry au">Maths</span>
            <span className="ctry us">Math</span> Doctor<sup>&reg;</sup> has
            diagnosed the following gaps in{" "}
            <span className="firstname" id="spanFname1">
              {student_info.FName}
            </span>
            ’s wall of knowledge:
          </p>
          <div id="gaps_box_container" style={{ margin: 'auto' }}>
            {gaps.map((g, i) => (
              <div className="gaps_box" key={i}>
                <div className="gaps_subtitle">
                  <p>
                    <span className="ctry au">Year</span>
                    <span className="ctry us">Grade</span>{" "}
                    <span className="gap_grade">{g.DisplayYear}</span>&nbsp;gaps
                  </p>
                </div>
                <p className="gaps_listing">{g.Gaps}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="footer_div">
        <p>
          Disclaimer: <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span> Doctor&reg; reports are to be
          used as a guideline only. No responsibility is taken if results
          provided by <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span> Doctor&reg; do not translate to
          school reports or any other forms of assessment. It is assumed that
          the user used <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span> Doctor&reg; in accordance with
          the guidelines specified.
        </p>
      </div>

      {alertInfo.show && (
        <CustomAlert
          message={alertInfo.message}
          onClose={() => setAlertInfo({ ...alertInfo, show: false })}
        />
      )}
    </>
  );
};

export default EStillReport;
